import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import * as _ from 'lodash-es';
import { GfTitleService } from './core.title.service';

/**
 * PageContextService tracks the current page context (project, section, element)
 * for building structured labels like "GForge - Development Tracker - Tracker Item #1235".
 *
 * Updated automatically via a ui-router transition hook — no per-component changes needed.
 */
@Injectable()
export class PageContextService {

    projectName: string = '';
    sectionLabel: string = '';
    elementLabel: string = '';

    // Notify subscribers when context changes
    contextChanged$: Subject<void> = new Subject<void>();

    // Human-readable section labels keyed by section type
    private readonly sectionLabelMap: { [key: string]: string } = {
        'trackeritem': 'Tracker Item',
        'tracker': 'Tracker',
        'wiki': 'Wiki',
        'frs': 'Releases',
        'docman': 'Documents',
        'forum': 'Discussions',
        'scm': 'Source Code',
        'project': 'Project Home',
        'sprint': 'Sprint',
        'standup': 'Team Standup',
        'board': 'Planning Board',
        'build': 'Builds',
        'report': 'Reports'
    };

    // Map ui-router state segments to section types
    private readonly stateToSectionMap: { [key: string]: string } = {
        'tracker.item': 'trackeritem',
        'trackeradmin': 'tracker',
        'tracker': 'tracker',
        'wiki': 'wiki',
        'frs': 'frs',
        'docman': 'docman',
        'forum': 'forum',
        'scm': 'scm',
        'board': 'board',
        'builds': 'build',
        'build': 'build',
        'sprint': 'sprint',
        'standup': 'standup',
        'reports': 'report',
        'report': 'report'
    };

    /**
     * Update context from a completed ui-router transition.
     * Uses the transition injector to read resolved data tokens.
     */
    updateFromTransition(transition: any): void {
        const stateName: string = transition.to().name || '';
        const params = transition.params();
        const injector = transition.injector();

        // Project name
        this.projectName = this.resolveProjectName(injector);

        // Section detection
        const sectionType = this.detectSection(stateName);

        // Section label: try to get specific name from resolved data
        this.sectionLabel = this.resolveSectionLabel(sectionType, injector, stateName);

        // Element label: the specific item/page being viewed
        this.elementLabel = this.resolveElementLabel(sectionType, injector, params);

        this.contextChanged$.next();
    }

    /**
     * Build a structured title: "Project - Section - Element"
     * Omits empty segments gracefully.
     */
    getStructuredTitle(): string {
        const parts: string[] = [];

        if (this.projectName) {
            parts.push(this.projectName);
        }
        if (this.sectionLabel) {
            parts.push(this.sectionLabel);
        }
        if (this.elementLabel) {
            parts.push(this.elementLabel);
        }

        if (parts.length > 0) {
            return parts.join(GfTitleService.SEPARATOR);
        }

        // Fallback: use document.title when transition hook hasn't populated context yet
        const docTitle = (document.title || '').trim();
        return docTitle || 'Untitled';
    }

    /**
     * Get the section type string (e.g. 'tracker', 'wiki') for the current state
     */
    getSectionType(stateName?: string): string {
        return this.detectSection(stateName || '');
    }

    /**
     * Get the default label for a section type
     */
    getDefaultSectionLabel(sectionType: string): string {
        return this.sectionLabelMap[sectionType] || '';
    }

    // Private helpers

    private resolveProjectName(injector: any): string {
        try {
            const project = injector.get('project');
            if (project && project.name) {
                return project.name;
            }
        } catch (e) {
            // Not in a project context
        }
        return '';
    }

    private detectSection(stateName: string): string {
        // Check longer patterns first (tracker.item before tracker)
        const sortedEntries = Object.entries(this.stateToSectionMap)
            .sort((a, b) => b[0].length - a[0].length);

        for (const [pattern, section] of sortedEntries) {
            if (stateName.includes(pattern)) {
                return section;
            }
        }

        // If we're in a project state but no specific section matched
        if (stateName.includes('home.project.summary')) {
            return 'project';
        }

        return 'page';
    }

    private resolveSectionLabel(sectionType: string, injector: any, stateName: string): string {
        // Try to get a specific name from resolved data
        try {
            switch (sectionType) {
                case 'tracker':
                case 'trackeritem': {
                    const tracker = injector.get('tracker');
                    if (tracker && tracker.trackerName) {
                        return tracker.trackerName;
                    }
                    // For trackeritem states, the tracker might be nested in trackeritem
                    const trackeritem = injector.get('trackeritem');
                    if (trackeritem && trackeritem.tracker && trackeritem.tracker.trackerName) {
                        return trackeritem.tracker.trackerName;
                    }
                    break;
                }
                case 'board': {
                    const board = injector.get('board');
                    if (board && board.boardName) {
                        return board.boardName;
                    }
                    break;
                }
                case 'forum': {
                    const forum = injector.get('forum');
                    if (forum && forum.forumName) {
                        return forum.forumName;
                    }
                    break;
                }
                case 'scm': {
                    const scm = injector.get('scm');
                    if (scm && scm.scmType) {
                        return _.startCase(scm.scmType) + ' Repository';
                    }
                    break;
                }
                case 'sprint': {
                    const sprint = injector.get('sprint');
                    if (sprint && sprint.sprintName) {
                        return sprint.sprintName;
                    }
                    break;
                }
            }
        } catch (e) {
        }

        return this.sectionLabelMap[sectionType] || '';
    }

    private resolveElementLabel(sectionType: string, injector: any, params: any): string {
        try {
            switch (sectionType) {
                case 'trackeritem': {
                    const trackeritem = injector.get('trackeritem');
                    if (trackeritem) {
                        const id = trackeritem.id || params.id || '';
                        const summary = trackeritem.summary || '';
                        if (id && summary) {
                            return '#' + id + ' ' + summary;
                        } else if (id) {
                            return '#' + id;
                        }
                    }
                    break;
                }
                case 'wiki': {
                    try {
                        const wikiPage = injector.get('wikiPage');
                        if (wikiPage && wikiPage.pageName) {
                            return wikiPage.pageName;
                        }
                    } catch (e) {
                        // wikiPage resolve not available
                    }
                    // Fall back to URL param
                    if (params.pagename) {
                        return decodeURIComponent(params.pagename);
                    }
                    break;
                }
                case 'board': {
                    // ToDo
                    break;
                }
                case 'forum': {
                    const forum = injector.get('forum');
                    if (forum && forum.subject) {
                        return forum.subject;
                    }
                    break;
                }
                case 'docman': {
                    try {
                        const data = injector.get('data');
                        if (data && data.folder && data.folder.folderName) {
                            return data.folder.folderName;
                        }
                    } catch (e) {
                        // data resolve not available
                    }
                    // Fall back to path param
                    if (params.path) {
                        const decoded = decodeURIComponent(params.path);
                        const segments = decoded.split('/').filter(Boolean);
                        if (segments.length > 0) {
                            return segments[segments.length - 1];
                        }
                    }
                    break;
                }
                case 'frs': {
                    // ToDo
                    break;
                }
                case 'sprint': {
                    // ToDo
                    break;
                }
            }
        } catch (e) {
        }

        return '';
    }
}
